import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE,
  timeout: 60000, // 60s timeout - Render free tier cold starts can take 50s+
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  }
});

// Retry once on network failures (handles Render free tier cold starts)
api.interceptors.response.use(null, async (error) => {
  const config = error.config;
  const isNetworkError = error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !error.response;
  if (!config._retried && isNetworkError) {
    config._retried = true;
    await new Promise(r => setTimeout(r, 3000));
    return api(config);
  }
  return Promise.reject(error);
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // FormData: must not send application/json Content-Type (no multipart boundary)
    if (config.data instanceof FormData) {
      const h = config.headers;
      if (h && typeof h.delete === 'function') {
        h.delete('Content-Type');
        h.delete('content-type');
      } else if (h) {
        delete h['Content-Type'];
        delete h['content-type'];
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const data = error.response?.data;
    const errorCode = data?.error;
    const errorMessage = data?.message;
    const isInvalidToken403 = status === 403 && (
      errorCode === 'Invalid Token' ||
      (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('token is invalid or expired'))
    );

    if (status === 403 && (data?.code === 'RESUME_LIMIT_REACHED' || data?.code === 'SUBSCRIPTION_REQUIRED')) {
      try {
        const raw = localStorage.getItem('user');
        const u = raw ? JSON.parse(raw) : {};
        if (['single', 'parent'].includes(u.role)) {
          error.__resumeLimitRedirected = true;
          window.location.assign('/subscription');
        }
      } catch (_) {
        /* ignore */
      }
    }

    if (status === 401 || isInvalidToken403) {
      // Clear auth state when backend rejects auth token.
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
